import React, { useState, useContext, useEffect } from "react";
import axios from "axios";
import { API, AuthContext } from "../App";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Check, Zap, ArrowRight, Loader2, Crown, Sparkles, PartyPopper, Clock, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { getActiveDiscount, applyDiscount } from "../utils/planDiscounts";
import { isIOSApp } from "../hooks/useIsMobileApp";

const PlanSelectionModal = ({ onClose, isFrozen = false, isExpired = false }) => {
  const { user, setUser } = useContext(AuthContext);
  const [activating, setActivating] = useState(false);
  const [checkingOut, setCheckingOut] = useState(null);
  const [trialActivated, setTrialActivated] = useState(false);
  const [trialEndsAt, setTrialEndsAt] = useState(null);
  const [plans, setPlans] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);

  useEffect(() => {
    axios.get(`${API}/plans`)
      .then(res => {
        const fetched = (res.data.plans || [])
          .filter(p =>
            p.plan_id !== "starter" &&
            !p.is_contact_us &&
            typeof p.price === "number" && p.price > 0
          )
          .map(p => ({
            id: p.plan_id,
            plan_id: p.plan_id,  // preserved for discount matching by plan_id
            name: p.name,
            price: p.price,
            popular: !!p.is_popular,
            features: p.features || [],
          }));
        setPlans(fetched);
        setDiscounts(res.data.discounts || []);
      })
      .catch(() => toast.error("Failed to load plans"))
      .finally(() => setLoadingPlans(false));
  }, []);

  const getAuthConfig = () => {
    const token = localStorage.getItem("token");
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const handleStartTrial = async () => {
    setActivating(true);
    try {
      const res = await axios.post(`${API}/auth/activate-trial`, {}, getAuthConfig());
      const endsAt = res.data.trial_ends_at;
      setTrialEndsAt(endsAt);
      setTrialActivated(true);
      // Update local user state
      const updated = { ...user, trial_activated: true, subscription_status: "trialing", trial_ends_at: endsAt, account_status: "active" };
      setUser(updated);
      localStorage.setItem("user", JSON.stringify(updated));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to activate trial");
    } finally {
      setActivating(false);
    }
  };

  const handleSelectPlan = async (planId) => {
    if (checkingOut) return;
    setCheckingOut(planId);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        plan_id: planId,
        origin_url: window.location.origin,
        billing_period: "monthly",
      }, getAuthConfig());
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error("Could not create checkout session. Please try again.");
        setCheckingOut(null);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start checkout. Please try again.");
      setCheckingOut(null);
    }
  };

  const formatTrialEnd = (isoDate) => {
    if (!isoDate) return "";
    try {
      const d = new Date(isoDate);
      return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
    } catch { return ""; }
  };

  const getDaysRemaining = (isoDate) => {
    if (!isoDate) return 7;
    try {
      const end = new Date(isoDate);
      const now = new Date();
      const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
      return Math.max(0, diff);
    } catch { return 7; }
  };

  // Never render purchase UI inside the iOS app (Apple Guideline 3.1.1).
  // DashboardLayout shows a neutral notice for frozen/expired accounts there.
  if (isIOSApp()) return null;

  // Trial activated success screen
  if (trialActivated) {
    const daysLeft = getDaysRemaining(trialEndsAt);
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="trial-welcome-screen">
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
        <div className="relative w-full max-w-md mx-4 rounded-2xl bg-[#0d0d1a] border border-white/10 shadow-2xl overflow-hidden">
          <div className="h-1.5 bg-gradient-to-r from-[#FFCC00] via-emerald-400 to-[#FFCC00]" />
          <div className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#FFCC00]/15 flex items-center justify-center">
              <PartyPopper className="w-8 h-8 text-[#FFCC00]" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2" data-testid="trial-welcome-title">
              Welcome to your Free Trial!
            </h2>
            <p className="text-white/60 text-sm mb-6">
              Your 7-day trial is now active. Explore everything Fieldz AI has to offer.
            </p>
            <div className="bg-white/5 rounded-xl border border-white/10 p-4 mb-6 space-y-3">
              <div className="flex items-center gap-3 text-left">
                <div className="w-10 h-10 rounded-lg bg-[#FFCC00]/10 flex items-center justify-center shrink-0">
                  <Clock className="w-5 h-5 text-[#FFCC00]" />
                </div>
                <div>
                  <p className="text-white text-sm font-semibold">{daysLeft} days remaining</p>
                  <p className="text-white/40 text-xs">Your trial is active now</p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-left">
                <div className="w-10 h-10 rounded-lg bg-[#FFCC00]/10 flex items-center justify-center shrink-0">
                  <CalendarDays className="w-5 h-5 text-[#FFCC00]" />
                </div>
                <div>
                  <p className="text-white text-sm font-semibold">Trial ends</p>
                  <p className="text-white/40 text-xs" data-testid="trial-end-date">{formatTrialEnd(trialEndsAt)}</p>
                </div>
              </div>
            </div>
            <p className="text-white/40 text-xs mb-5">
              Choose a paid plan before your trial ends to keep using all features without interruption.
            </p>
            <Button
              onClick={onClose}
              className="w-full bg-[#FFCC00] hover:bg-[#FFCC00]/90 text-black font-semibold py-5"
              data-testid="trial-get-started-btn"
            >
              Get Started <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="plan-selection-modal">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

      {/* Modal */}
      <div className="relative w-full max-w-4xl mx-4 max-h-[92vh] overflow-y-auto rounded-2xl bg-[#0d0d1a] border border-white/10 shadow-2xl">
        {/* Top accent */}
        <div className="h-1 bg-gradient-to-r from-[#FFCC00] via-[#0046BE] to-[#FFCC00] rounded-t-2xl" />

        <div className="p-6 md:p-8">
          {/* Header */}
          <div className="text-center mb-8">
            {isFrozen && (
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/10 border border-red-500/30 mb-4">
                <Clock className="w-4 h-4 text-red-400" />
                <span className="text-red-400 text-sm font-medium">Your trial has expired</span>
              </div>
            )}
            {isExpired && !isFrozen && (
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/30 mb-4">
                <Clock className="w-4 h-4 text-amber-400" />
                <span className="text-amber-400 text-sm font-medium">Your subscription has expired</span>
              </div>
            )}
            {!isFrozen && !isExpired && (
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFCC00]/10 border border-[#FFCC00]/20 mb-4">
                <Sparkles className="w-4 h-4 text-[#FFCC00]" />
                <span className="text-[#FFCC00] text-sm font-medium">Welcome to Fieldz AI</span>
              </div>
            )}
            <h2 className="text-2xl md:text-3xl font-bold text-white font-['Manrope'] mb-2">
              {isFrozen ? "Upgrade to Continue" : isExpired ? "Renew Your Subscription" : "Choose Your Plan"}
            </h2>
            <p className="text-white/50 text-sm md:text-base max-w-lg mx-auto">
              {isFrozen
                ? "Your free trial has ended. Choose a plan to restore access to your account and all its data."
                : isExpired
                ? "Your plan has expired. Subscribe to a monthly plan to keep using all features and access your data."
                : "Start with a 7-day free trial or pick the plan that fits your business. You can upgrade anytime."}
            </p>
          </div>

          {/* Plans Grid */}
          {loadingPlans ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#FFCC00]" />
            </div>
          ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {plans.map((plan) => {
              const planDiscount = getActiveDiscount(plan, discounts);
              const finalPrice = planDiscount ? applyDiscount(plan.price, planDiscount) : plan.price;
              const hasDiscount = !!planDiscount && finalPrice < plan.price;
              return (
              <div
                key={plan.id}
                className={`relative rounded-xl border p-5 transition-all cursor-pointer hover:scale-[1.02] ${
                  plan.popular
                    ? "border-[#0046BE] bg-[#0046BE]/10 shadow-lg shadow-[#0046BE]/10"
                    : "border-white/10 bg-white/[0.03] hover:border-white/20"
                }`}
                data-testid={`plan-card-${plan.id}`}
                onClick={() => handleSelectPlan(plan.id)}
              >
                {plan.popular && (
                  <Badge className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-[#0046BE] text-white text-xs px-3">
                    Most Popular
                  </Badge>
                )}
                {hasDiscount && planDiscount.badge_label && !plan.popular && (
                  <Badge
                    className="absolute -top-2.5 right-3 bg-red-500/20 text-red-300 border border-red-500/40 text-[10px] uppercase tracking-wider"
                    data-testid={`plan-discount-badge-${plan.id}`}
                  >
                    {planDiscount.badge_label}
                  </Badge>
                )}
                <div className="text-center mb-4 pt-1">
                  <h3 className="text-white font-semibold text-lg">{plan.name}</h3>
                  <div className="mt-2">
                    {hasDiscount && (
                      <span
                        className="text-lg text-white/40 line-through mr-2"
                        data-testid={`plan-original-price-${plan.id}`}
                      >
                        ${plan.price}
                      </span>
                    )}
                    <span
                      className={`text-3xl font-bold ${hasDiscount ? "text-[#FFCC00]" : "text-white"}`}
                      data-testid={`plan-final-price-${plan.id}`}
                    >
                      ${finalPrice}
                    </span>
                    <span className="text-white/40 text-sm">/mo</span>
                  </div>
                  {hasDiscount && planDiscount.marketing_text && (
                    <p
                      className="text-[11px] font-semibold text-red-300 mt-1"
                      data-testid={`plan-discount-text-${plan.id}`}
                    >
                      {planDiscount.marketing_text}
                    </p>
                  )}
                </div>
                <ul className="space-y-2 mb-5">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-white/70">
                      <Check className="w-3.5 h-3.5 text-[#FFCC00] shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Button
                  disabled={checkingOut === plan.id}
                  className={`w-full ${
                    plan.popular
                      ? "bg-[#0046BE] hover:bg-[#0046BE]/80 text-white"
                      : "bg-white/10 hover:bg-white/15 text-white"
                  }`}
                  data-testid={`select-plan-${plan.id}`}
                >
                  {checkingOut === plan.id ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting...</>
                  ) : (
                    <>Choose {plan.name} <ArrowRight className="w-4 h-4 ml-1" /></>
                  )}
                </Button>
              </div>
            );
            })}
          </div>
          )}

          {/* Free Trial CTA - only show if not frozen and not expired */}
          {!isFrozen && !isExpired && (
            <div className="rounded-xl border border-[#FFCC00]/20 bg-[#FFCC00]/5 p-5 text-center">
              <div className="flex items-center justify-center gap-3 mb-2">
                <Crown className="w-5 h-5 text-[#FFCC00]" />
                <h3 className="text-white font-semibold text-lg">Not ready to commit?</h3>
              </div>
              <p className="text-white/50 text-sm mb-4">
                Start a <strong className="text-white">7-day free trial</strong> with Starter limits (100 SMS, 100 min, 1 line). No credit card required.
              </p>
              <Button
                onClick={handleStartTrial}
                disabled={activating}
                className="bg-[#FFCC00] hover:bg-[#FFCC00]/90 text-black font-semibold px-8 py-5"
                data-testid="start-trial-btn"
              >
                {activating ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Zap className="w-4 h-4 mr-2" />
                )}
                Start Free Trial
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlanSelectionModal;
